import React, { useState, useEffect } from 'react';
import Grid from '@material-ui/core/Grid';
import Box from '@material-ui/core/Box';
import { useHistory, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';

// components
import { Typography } from '../../components/Wrappers/Wrappers';
import Widget from '../../components/Widget/Widget';
import { Breadcrumb, FormFooter, CaseDesignRefine, CaseMethod, FileAttachment, OrderConfirmation } from './components';

// context
import { useOrderDispatch, useOrderState, actions as orderActions } from '../../context/OrderContext';
import { useUserState } from '../../context/UserContext';

// form helpers
import {
    emptyOrder,
    novaRefinementFormStepsTitle,
    initNovaRefinement,
    caseDesignAttributes,
    findMissingRequiredFields,
    hasMissingRequiredFields,
    impressionMethod,
    transferMethod,
} from './helpers/form';

const RefineNewOrder = () => {
    const orderDispatch = useOrderDispatch();
    const orderState = useOrderState();
    const userState = useUserState();

    const history = useHistory();
    const location = useLocation();

    const [refinementOrder, setRefinementOrder] = useState(emptyOrder());
    const [activeStep, setActiveStep] = useState(0);
    const [error, setError] = useState({});

    const allowFullArchTreatment = userState.currentUser?.practice?.allowFullArchTreatment === 'Yes' ?? false;

    // ===== Hooks =====

    useEffect(() => {
        orderActions.doNewForm()(orderDispatch);
    }, []); // eslint-disable-line

    // Load the target order and practice from query string.
    // orderId refers to the source order
    useEffect(() => {
        const queryString = new URLSearchParams(location.search);
        const orderId = queryString.get('orderId');
        if (!orderId) {
            toast.error('Unable to find order id');
            setTimeout(() => history.goBack(), 2000);
        } else {
            if (!orderState.currentOrder || orderState.currentOrder.orderId !== orderId) {
                orderActions.doFind(orderId)(orderDispatch);
            }
        }
    }, [location.search]); // eslint-disable-line react-hooks/exhaustive-deps

    // Pre-load form state once the order is available.
    // Derive a fresh refinement skeleton from the source order.
    useEffect(() => {
        if (orderState.currentOrder && refinementOrder?.orderId !== orderState.currentOrder.orderId) {
            setRefinementOrder(initNovaRefinement(orderState.currentOrder));
        }
    }, [orderState.currentOrder]); // eslint-disable-line react-hooks/exhaustive-deps

    // ===== Navigation =====

    const goNext = (step = 1) => setActiveStep((prev) => prev + step);
    const goBack = (step = 1) => setActiveStep((prev) => prev - step);

    const checkCaseDesign = () => {
        // Validate standard design fields (treatmentType is already set, arches is pre-set or locked)
        const standardFields = caseDesignAttributes(refinementOrder, allowFullArchTreatment).filter(
            (attr) => attr.id !== 'treatmentType'
        );
        const standardErrors = findMissingRequiredFields(standardFields, refinementOrder);

        // Validate refinement-specific required fields
        const refinementErrors = {};

        const issueDesc = refinementOrder.refinementIssueDescription || '';
        if (!issueDesc.trim() || issueDesc.length > 800) {
            refinementErrors.refinementIssueDescription = true;
        }

        // Retainer questions are required when the current arches value is a single arch
        const currentArches = refinementOrder.arches || '';
        if (currentArches === 'Upper only' && !refinementOrder.includeLowerArchRetainer) {
            refinementErrors.includeLowerArchRetainer = true;
        }
        if (currentArches === 'Lower only' && !refinementOrder.includeUpperArchRetainer) {
            refinementErrors.includeUpperArchRetainer = true;
        }

        const allErrors = { ...standardErrors, ...refinementErrors };
        setError({ ...error, ...allErrors });
        if (!hasMissingRequiredFields(allErrors)) goNext();
    };

    const checkMethod = () => {
        if (!refinementOrder.method) {
            toast.error('Please select an option');
        } else {
            goNext();
        }
    };

    const checkFileAttachment = () => {
        const hasPhotos =
            orderState.photosUpload &&
            orderState.photosUpload.uploadedFiles &&
            orderState.photosUpload.uploadedFiles.length > 0;

        if (refinementOrder.method === impressionMethod || refinementOrder.method === transferMethod) {
            if (!hasPhotos) {
                toast.error("Please upload the patient's photos");
                return;
            }
            doSubmit(refinementOrder);
            goNext();
            return;
        }

        // Scan method — 3D files are also required
        const hasMaxillaryArch =
            orderState.maxillaryArchUpload &&
            orderState.maxillaryArchUpload.uploadedFiles &&
            orderState.maxillaryArchUpload.uploadedFiles.length > 0;
        const hasMandibularArch =
            orderState.mandibularArchUpload &&
            orderState.mandibularArchUpload.uploadedFiles &&
            orderState.mandibularArchUpload.uploadedFiles.length > 0;

        if (!hasMaxillaryArch) {
            toast.error('Please upload the upper arch file');
            return;
        }
        if (!hasMandibularArch) {
            toast.error('Please upload the lower arch file');
            return;
        }

        doSubmit(refinementOrder);
        goNext();
    };

    const checkOrderConfirmation = () => history.push('/app/dashboard');

    const formStepsValidation = [checkCaseDesign, checkMethod, checkFileAttachment, checkOrderConfirmation];

    // ===== Handlers =====

    const handleTextFieldChange = (e) => {
        if (e.target.required) setError({ ...error, [e.target.name]: !e.target.value });

        const updates = { [e.target.name]: e.target.value };
        if (e.target.name === 'arches') {
            if (e.target.value === 'Upper only') updates.lowerMidline = '';
            else if (e.target.value === 'Lower only') updates.upperMidline = '';
        }
        setRefinementOrder({ ...refinementOrder, ...updates });
    };

    const handleMethodClicked = (method) => (_) => setRefinementOrder({ ...refinementOrder, method });

    const handleNext = async () => formStepsValidation[activeStep]();
    const handleBack = () => goBack();

    const doSubmit = async (formValues) => {
        const orderId = new URLSearchParams(location.search).get('orderId');

        const body = {
            orderType: formValues.orderType,
            practiceId: formValues.practiceId,
            treatmentType: formValues.treatmentType,
            mainComplaints: formValues.mainComplaints,
            arches: formValues.arches,
            allowIPR: formValues.allowIPR,
            allowAttachment: formValues.allowAttachment,
            allowElastics: formValues.allowElastics,
            allowTADs: formValues.allowTADs,
            ...(formValues.upperMidline && { upperMidline: formValues.upperMidline }),
            ...(formValues.lowerMidline && { lowerMidline: formValues.lowerMidline }),
            attachmentTrayMaterial: formValues.attachmentTrayMaterial,
            trimLine: formValues.trimLine,
            serialNumber: formValues.serialNumber,
            method: formValues.method,
            refinementIssueDescription: formValues.refinementIssueDescription,
            refinementProblemTeeth: formValues.refinementProblemTeeth || [],
            // Last aligner worn questions — only included when a value was set
            ...(formValues.lastUpperAlignerWorn && {
                lastUpperAlignerWorn: formValues.lastUpperAlignerWorn,
            }),
            ...(formValues.lastLowerAlignerWorn && {
                lastLowerAlignerWorn: formValues.lastLowerAlignerWorn,
            }),
            // Retainer questions — only included when a value was set
            ...(formValues.includeLowerArchRetainer && {
                includeLowerArchRetainer: formValues.includeLowerArchRetainer,
            }),
            ...(formValues.includeUpperArchRetainer && {
                includeUpperArchRetainer: formValues.includeUpperArchRetainer,
            }),
            photosUpload: orderState.photosUpload,
            maxillaryArchUpload: orderState.maxillaryArchUpload,
            mandibularArchUpload: orderState.mandibularArchUpload,
        };

        await orderActions.doRefineNewOrder(orderId, body)(orderDispatch);
    };

    const formStepsComponent = [
        <CaseDesignRefine
            refinementOrder={refinementOrder}
            allowFullArchTreatment={allowFullArchTreatment}
            formErrors={error}
            onTextFieldChange={handleTextFieldChange}
        />,
        <CaseMethod newOrder={refinementOrder} methodClicked={handleMethodClicked} />,
        <FileAttachment newOrder={refinementOrder} />,
        <OrderConfirmation newOrder={refinementOrder} />,
    ];

    return (
        <Grid container spacing={3}>
            <Breadcrumb activeStep={activeStep} stepsTitle={novaRefinementFormStepsTitle} />
            <Grid item xs={12}>
                <Widget>
                    <Grid item justifyContent="center" container>
                        <Box display="flex" flexDirection="column" width={600}>
                            <Typography variant="h5" weight="medium" style={{ marginBottom: 30 }}>
                                {novaRefinementFormStepsTitle[activeStep]}
                            </Typography>
                            {formStepsComponent[activeStep]}
                            <FormFooter
                                activeStep={activeStep}
                                isRefinement={true}
                                onClickNext={handleNext}
                                onClickBack={handleBack}
                            />
                        </Box>
                    </Grid>
                </Widget>
            </Grid>
        </Grid>
    );
};

export default RefineNewOrder;
